import { useCallback } from 'react'

import { FAVORITES_SCROLL } from '../constants/favoritesConfig.js'

/**
 * Computes the drag-scroll speed multiplier based on the favorites count.
 *
 * @param {number} favoritesCount
 * @returns {number}
 */
export function getFavoritesScrollSpeedMultiplier(favoritesCount) {
  const safeCount = Number.isFinite(favoritesCount) ? favoritesCount : 0
  const extraMultiplier = Math.min(
    FAVORITES_SCROLL.EXTRA_SPEED_MAX,
    Math.max(0, (safeCount - 5) * FAVORITES_SCROLL.EXTRA_SPEED_STEP_AFTER_FIFTH),
  )

  return FAVORITES_SCROLL.BASE_SPEED_MULTIPLIER + extraMultiplier
}

/**
 * Returns a stable callback that wires horizontal drag and wheel scrolling
 * on the provided container element.
 *
 * @param {{ favoritesCount?: number }} params
 * @returns {{ wireScrollListeners: (element: HTMLElement | null) => (() => void) }}
 */
export function useFavoritesScrolling({ favoritesCount = 0 } = {}) {
  const wireScrollListeners = useCallback(
    (element) => {
      if (!element) return () => {}

      let isDragging = false
      let startX = 0
      let scrollLeftStart = 0

      const handleWheel = (event) => {
        if (element.scrollWidth <= element.clientWidth) return
        if (event.deltaY === 0) return

        const currentScrollLeft = element.scrollLeft
        const maxScrollLeft = element.scrollWidth - element.clientWidth
        const atLeftEdge = currentScrollLeft <= 0
        const atRightEdge = currentScrollLeft >= maxScrollLeft - 1

        if ((event.deltaY < 0 && atLeftEdge) || (event.deltaY > 0 && atRightEdge)) {
          return
        }

        event.preventDefault()
        element.scrollLeft += event.deltaY
      }

      const handleMouseDown = (event) => {
        if (event.button !== 0) return
        isDragging = true
        startX = event.pageX
        scrollLeftStart = element.scrollLeft
        element.style.cursor = 'grabbing'
        element.style.userSelect = 'none'
      }

      const stopDragging = () => {
        if (!isDragging) return
        isDragging = false
        element.style.cursor = 'grab'
        element.style.userSelect = ''
      }

      const handleMouseMove = (event) => {
        if (!isDragging) return
        event.preventDefault()
        const speedMultiplier = getFavoritesScrollSpeedMultiplier(favoritesCount)
        const walk = (event.pageX - startX) * speedMultiplier
        element.scrollLeft = scrollLeftStart - walk
      }

      element.addEventListener('wheel', handleWheel, { passive: false })
      element.addEventListener('mousedown', handleMouseDown)
      element.addEventListener('mouseleave', stopDragging)
      element.addEventListener('mouseup', stopDragging)
      element.addEventListener('mousemove', handleMouseMove)

      return () => {
        element.removeEventListener('wheel', handleWheel)
        element.removeEventListener('mousedown', handleMouseDown)
        element.removeEventListener('mouseleave', stopDragging)
        element.removeEventListener('mouseup', stopDragging)
        element.removeEventListener('mousemove', handleMouseMove)
        element.style.cursor = ''
        element.style.userSelect = ''
      }
    },
    [favoritesCount],
  )

  return { wireScrollListeners }
}
